import { action, computed, makeObservable, observable } from 'mobx';

import { equalsDeep } from 'src/packages/utils/equals-deep';
import { isObjectWithKeys } from 'src/packages/utils/is-object-with-keys';

import type { TControlView, TFieldRulesData } from '../types';

export class DirectoryRecord {
  readonly recordId: number | string;
  readonly initialData: Record<string, unknown> | null;
  readonly controlViews: TControlView[];
  @observable.ref data: Record<string, unknown> = {};
  @observable fieldRulesData: Record<string, TFieldRulesData | undefined> = {};
  @observable.ref fieldsErrors: Record<string, string[] | undefined> = {};

  constructor(controlViews: TControlView[], initialData: Record<string, unknown> | null, id: number | string) {
    this.recordId = id;
    this.data = initialData ?? {};
    this.initialData = initialData;
    this.controlViews = controlViews;

    makeObservable(this);
  }

  @action.bound
  updateData(data: Record<string, unknown>): void {
    this.data = { ...this.data, ...data };
  }

  @action.bound
  updateFieldRulesData(data: Record<string, TFieldRulesData | undefined>): void {
    this.fieldRulesData = data;
  }

  @action.bound
  updateFieldsErrors(data: Record<string, string[] | undefined>) {
    this.fieldsErrors = data;
  }

  @computed
  get updatedFields(): Record<string, unknown> {
    const updatedFields: Record<string, unknown> = {};
    for (const view of this.controlViews) {
      if (!view.attrName) {
        continue;
      }

      if (!equalsDeep(this.data[view.fieldId], this.initialData?.[view.fieldId])) {
        updatedFields[view.fieldId] = this.data[view.fieldId];
      }
    }

    return updatedFields;
  }

  @computed
  get recordChangeState(): 'new' | 'hasChanges' | null {
    if (typeof this.recordId === 'number' && this.recordId < 0) {
      return 'new';
    }

    if (isObjectWithKeys(this.updatedFields)) {
      return 'hasChanges';
    }

    return null;
  }
}
